import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import CheckmarkIcon from "@src/lib/icons/18px/checkmark";
import FileOutlineIcon from "@src/lib/icons/18px/file-outline";
import XIcon from "@src/lib/icons/18px/x";
import {
  APPROVE_PLAN_MESSAGE,
  type PlanProposalState,
  groupPlanItems,
  planKindHeading,
  planProposalStates,
} from "./planProposals";
import {
  OPUS_AGENT_CANVAS_SLOT_ID,
  canShowOpusAgentCanvas,
  useOpusAgentCanvasStore,
} from "./useOpusAgentCanvasStore";
import type { OpusAgentTurnState } from "./useOpusAgentChat";

const ENGLISH_ONLY =
  /^(?:english|en)(?:\s*\((?:us|uk|u\.s\.)\))?(?:\s+only)?\.?$/i;
const isEnglishOnly = (language: string): boolean =>
  ENGLISH_ONLY.test(language.trim());

// Page.tsx puts this empty slot inside the main page card (SidebarInset);
// the canvas portals into it and covers only that card. z-[800] clears the
// card's own floating chrome (the builders' bottom toolbar and zoom pill are
// z-[750]); Page.tsx isolates the card while the canvas is open, so every
// body-portaled dialog, popover, menu and toast still sits above it.
export { OPUS_AGENT_CANVAS_SLOT_ID };

export const OpusAgentCanvasSlot: React.FC = () => (
  <div id={OPUS_AGENT_CANVAS_SLOT_ID} className="contents" />
);

// Wraps the page card's content under the canvas: while the canvas is open
// it is inert, so keyboard and screen-reader users can't reach what they
// can't see. display: contents keeps the card's layout untouched.
export const OpusAgentCanvasCovered: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const canvasOpen = useOpusAgentCanvasStore((store) => store.view !== null);
  return (
    <div
      className="contents"
      data-testid="opus-agent-canvas-covered"
      inert={canvasOpen}
    >
      {children}
    </div>
  );
};

const COMPOSER_SELECTOR = '[data-testid="chat-composer-input"]';

// Escape closes the canvas first, before the agent panel's own Escape (it
// checks defaultPrevented). Menus, dialogs and fields other than the
// composer keep their Escape (mirrors the panel's escapeClosesPanel).
const escapeClosesCanvas = (event: KeyboardEvent): boolean => {
  if (event.key !== "Escape" || event.defaultPrevented) return false;
  const target = event.target as HTMLElement | null;
  if (
    target?.closest?.(
      '[role="menu"],[role="dialog"],[role="alertdialog"],[role="listbox"]',
    )
  )
    return false;
  if (
    target?.closest?.('[data-state="delayed-open"],[data-state="instant-open"]')
  )
    return false;
  if (target instanceof HTMLInputElement) return false;
  if (
    target instanceof HTMLTextAreaElement &&
    target.dataset.testid !== "chat-composer-input"
  )
    return false;
  if (
    target?.isContentEditable ||
    target?.closest?.('[contenteditable]:not([contenteditable="false"])')
  )
    return false;
  return !document.querySelector(
    '[role="dialog"][data-state="open"],[role="alertdialog"][data-state="open"],[role="menu"][data-state="open"]',
  );
};

interface OpusAgentCanvasProps {
  state: PlanProposalState;
  onApprove: () => void;
  onClose: () => void;
  // Move focus into the canvas on mount (the person opened it). A live plan
  // that opens it on its own leaves focus in the composer.
  autoFocus?: boolean;
}

// A read-only plan document over the main page card. People change the
// plan by chat or voice; there is no editing here.
export const OpusAgentCanvas: React.FC<OpusAgentCanvasProps> = ({
  state,
  onApprove,
  onClose,
  autoFocus = false,
}) => {
  const { plan } = state;
  const sectionRef = useRef<HTMLElement | null>(null);
  // Focus goes back where it was when the canvas opened (the card's View
  // plan button, or the composer), else to the composer, not to body.
  const openerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const active = document.activeElement;
    openerRef.current =
      active instanceof HTMLElement && active !== document.body ? active : null;
    // After the opener is captured, so close still returns focus to it.
    if (autoFocus) sectionRef.current?.focus();
    // Mount-only: a plan switch remounts the canvas (keyed by event id).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const closeAndRestoreFocus = useCallback(() => {
    const opener = openerRef.current;
    onClose();
    const target =
      opener?.isConnected === true
        ? opener
        : document.querySelector<HTMLElement>(COMPOSER_SELECTOR);
    target?.focus();
  }, [onClose]);
  // Approve closes the canvas too, so focus goes back the same way.
  const approveAndRestoreFocus = useCallback(() => {
    onApprove();
    closeAndRestoreFocus();
  }, [onApprove, closeAndRestoreFocus]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!escapeClosesCanvas(event)) return;
      event.preventDefault();
      closeAndRestoreFocus();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [closeAndRestoreFocus]);
  const kindHeading = planKindHeading(plan.kind);
  const itemGroups = groupPlanItems(plan.items);
  const hasSections = itemGroups.some((group) => group.section !== null);
  const changeCount = plan.changes.length;
  const meta: { label: string; value: React.ReactNode }[] = [];
  if (plan.audience) meta.push({ label: "For", value: plan.audience });
  if (plan.sources.length > 0)
    meta.push({
      label: "Sources",
      value: (
        <div className="flex flex-wrap gap-1">
          {plan.sources.map((source) => (
            <span
              key={source}
              data-testid="opus-agent-canvas-source"
              className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-md bg-bg-primary px-1.5 py-0.5 shadow-default"
            >
              <FileOutlineIcon className="size-3 shrink-0 text-icon-secondary" />
              <Text
                variant="fine-print-description"
                className="min-w-0 truncate text-text-primary"
              >
                {source}
              </Text>
            </span>
          ))}
        </div>
      ),
    });
  // English is the default, so only another language the admin asked for
  // shows (plans from before the agent left English out still say it).
  if (plan.language && !isEnglishOnly(plan.language))
    meta.push({ label: "Language", value: plan.language });

  return (
    <section
      ref={sectionRef}
      tabIndex={-1}
      aria-label={`Plan: ${plan.title}`}
      data-testid="opus-agent-canvas"
      className="absolute inset-0 z-[800] flex min-w-0 flex-col overflow-hidden bg-bg-primary duration-200 animate-in outline-none fade-in motion-reduce:animate-none"
    >
      {/* Just the plan and the decision: the chat carries its status. */}
      <div className="flex h-13 min-w-0 shrink-0 items-center gap-2 border-b border-border-primary pr-3 pl-5">
        <Text
          variant="compact-description"
          className="shrink-0 text-text-tertiary"
          data-testid="opus-agent-canvas-version"
        >
          {plan.version > 1 ? `Plan v${plan.version}` : "Plan"}
        </Text>
        <Text
          variant="compact-title"
          className="min-w-0 flex-1 truncate text-text-primary"
        >
          {plan.title}
        </Text>
        <div className="flex shrink-0 items-center gap-1">
          {state.actionable && (
            <Button
              size="sm"
              onClick={approveAndRestoreFocus}
              data-testid="opus-agent-canvas-approve"
              className="mr-1"
            >
              <CheckmarkIcon data-icon="inline-start" />
              Approve &amp; build
            </Button>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={closeAndRestoreFocus}
                aria-label="Close canvas"
                data-testid="opus-agent-canvas-close"
              >
                <XIcon className="text-icon-secondary" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Close</TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-180 flex-col gap-8 px-8 pt-10 pb-12">
          <div className="flex flex-col gap-2">
            <Text variant="compact-description" className="text-text-tertiary">
              {kindHeading ? `${kindHeading} · draft` : "Draft"}
            </Text>
            <Text
              variant="page-title"
              multiline
              role="heading"
              aria-level={1}
              className="text-text-primary"
            >
              {plan.title}
            </Text>
            {plan.summary && (
              <Text
                variant="body-description"
                multiline
                className="text-text-secondary"
              >
                {plan.summary}
              </Text>
            )}
          </div>
          {meta.length > 0 && (
            <dl
              data-testid="opus-agent-canvas-meta"
              className="grid grid-cols-[6rem_minmax(0,1fr)] items-baseline gap-x-3 gap-y-3 rounded-2xl bg-bg-secondary p-4"
            >
              {meta.map(({ label, value }) => (
                <React.Fragment key={label}>
                  <dt>
                    <Text
                      variant="compact-description"
                      className="text-text-tertiary"
                    >
                      {label}
                    </Text>
                  </dt>
                  <dd className="min-w-0">
                    {typeof value === "string" ? (
                      <Text
                        variant="compact-description"
                        multiline
                        className="text-text-primary"
                      >
                        {value}
                      </Text>
                    ) : (
                      value
                    )}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          )}
          {plan.version > 1 && changeCount > 0 && (
            <CanvasSection title="What changed">
              <ul
                data-testid="opus-agent-canvas-changes"
                className="flex flex-col gap-1.5"
              >
                {plan.changes.map((change, index) => (
                  <li key={index} className="flex min-w-0 gap-2.5">
                    <span
                      aria-hidden
                      className="mt-2 size-1.5 shrink-0 rounded-full bg-icon-tertiary"
                    />
                    <Text
                      variant="compact-description"
                      multiline
                      className="min-w-0 text-text-secondary"
                    >
                      {change}
                    </Text>
                  </li>
                ))}
              </ul>
            </CanvasSection>
          )}
          {plan.items.length > 0 && (
            <CanvasSection title="Structure">
              {/* No sections: one plain list, as before sections existed. */}
              <div className={hasSections ? "flex flex-col gap-5" : "contents"}>
                {itemGroups.map((group) => {
                  const firstIndex = group.items[0].index;
                  const list = (
                    <ol
                      start={firstIndex > 0 ? firstIndex + 1 : undefined}
                      className="flex flex-col gap-2"
                    >
                      {group.items.map(({ item, index }) => (
                        <li
                          key={index}
                          data-testid="opus-agent-canvas-item"
                          data-new={item.isNew ? "true" : undefined}
                          className="flex min-w-0 gap-3 rounded-2xl bg-bg-secondary p-4"
                        >
                          {/* The same white tile as the thread's cards. */}
                          <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-bg-primary text-text-secondary shadow-default">
                            <Text variant="fine-print-title">{index + 1}</Text>
                          </span>
                          <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
                            <div className="flex min-w-0 items-start gap-2">
                              <Text
                                variant="compact-title"
                                multiline
                                className="min-w-0 text-text-primary"
                              >
                                {item.title}
                              </Text>
                              {item.isNew && (
                                <Text
                                  variant="fine-print-title"
                                  className="shrink-0 rounded-full bg-bg-primary px-2 py-0.5 text-status-success-text shadow-default"
                                >
                                  New
                                </Text>
                              )}
                              {item.kind && (
                                <Text
                                  variant="fine-print-description"
                                  className="ml-auto shrink-0 pt-0.5 text-text-tertiary"
                                >
                                  {item.kind}
                                </Text>
                              )}
                            </div>
                            {item.summary && (
                              <Text
                                variant="helper-description"
                                multiline
                                className="text-text-secondary"
                              >
                                {item.summary}
                              </Text>
                            )}
                            {item.points.length > 0 && (
                              <ul className="mt-1 list-disc pl-4.5 text-text-secondary">
                                {item.points.map((point, pointIndex) => (
                                  <li key={pointIndex}>
                                    <Text
                                      variant="fine-print-description"
                                      multiline
                                      className="leading-5"
                                    >
                                      {point}
                                    </Text>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </li>
                      ))}
                    </ol>
                  );
                  if (group.section === null)
                    return (
                      <React.Fragment key={firstIndex}>{list}</React.Fragment>
                    );
                  // A section reads like the canvas's other labels (the
                  // kind line, the details' "For"): muted, under Structure.
                  return (
                    <section
                      key={firstIndex}
                      data-testid="opus-agent-canvas-section"
                      aria-label={group.section}
                      className="flex flex-col gap-2"
                    >
                      <Text
                        variant="compact-description"
                        role="heading"
                        aria-level={3}
                        multiline
                        data-testid="opus-agent-canvas-section-heading"
                        className="px-1 text-text-tertiary"
                      >
                        {group.section}
                      </Text>
                      {list}
                    </section>
                  );
                })}
              </div>
            </CanvasSection>
          )}
        </div>
      </div>
    </section>
  );
};

const CanvasSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section className="flex flex-col gap-3">
    <Text
      variant="compact-title"
      role="heading"
      aria-level={2}
      className="text-text-primary"
    >
      {title}
    </Text>
    {children}
  </section>
);

interface OpusAgentCanvasHostProps {
  // The chat the panel is showing; a view for any other chat closes.
  chatId: string | null;
  turns: OpusAgentTurnState[];
  onSendMessage: (message: string) => void;
  // False for an archived or unavailable agent: the plan still reads, as
  // "Proposed", with no Approve.
  canSend?: boolean;
}

// Shows the canvas for the panel's chat, in the page card's slot.
export const OpusAgentCanvasHost: React.FC<OpusAgentCanvasHostProps> = ({
  chatId,
  turns,
  onSendMessage,
  canSend = true,
}) => {
  const view = useOpusAgentCanvasStore((store) => store.view);
  const close = useOpusAgentCanvasStore((store) => store.close);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const viewOpen = !!view;
  // Looked up after commit: the slot is a later sibling of the panel.
  useEffect(() => {
    setSlot(
      viewOpen ? document.getElementById(OPUS_AGENT_CANVAS_SLOT_ID) : null,
    );
  }, [viewOpen]);
  const states = useMemo(
    () => planProposalStates(turns, { canSend }),
    [turns, canSend],
  );
  const state =
    view && view.chatId === chatId ? states.get(view.eventId) : undefined;
  // A view this host can't show (its plan is gone, another chat's, or the
  // page has no slot) closes, so Page.tsx doesn't keep the card isolated.
  const hasState = !!state;
  useEffect(() => {
    if (viewOpen && (!hasState || !canShowOpusAgentCanvas())) close();
  }, [viewOpen, hasState, close]);
  if (!state || !slot) return null;
  const approve = () => {
    if (canSend) onSendMessage(APPROVE_PLAN_MESSAGE);
  };
  return createPortal(
    <OpusAgentCanvas
      key={state.eventId}
      state={state}
      onApprove={approve}
      onClose={close}
      autoFocus={view?.focus}
    />,
    slot,
  );
};
